import { useState } from 'react';
import {
  useActivityDependencies,
  useAddDependency,
  useDeleteDependency,
} from '@/hooks/useVcfAdmin';

export function DependencyPicker({
  capabilityId,
  activityId,
  siblingActivities,
}: {
  capabilityId: string;
  activityId: string;
  siblingActivities: Array<{ id: string; name: string }>;
}) {
  const { data } = useActivityDependencies(capabilityId, activityId);
  const addDep = useAddDependency();
  const deleteDep = useDeleteDependency();
  const [predecessorId, setPredecessorId] = useState('');
  const [depType, setDepType] = useState<'FS' | 'SS'>('FS');
  const [lagDays, setLagDays] = useState('0');

  const deps: Array<{ id: string; activityId: string; predecessorId: string; dependencyType: string; lagDays: number }> = data?.dependencies || [];

  return (
    <div>
      <label className="label">Dependencies</label>
      <div className="mt-1 space-y-2">
        {deps.length > 0 && (
          <div className="space-y-1">
            {deps.map((dep) => {
              const predName = siblingActivities.find(a => a.id === dep.predecessorId)?.name || dep.predecessorId;
              return (
                <div key={dep.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded px-2 py-1 text-xs">
                  <span className="truncate flex-1">
                    <span className="font-mono text-xtravirt-cyan">{dep.dependencyType}</span>{' '}
                    {predName}
                    {dep.lagDays > 0 && <span className="text-gray-400 ml-1">+{dep.lagDays}d lag</span>}
                  </span>
                  <button
                    onClick={() => deleteDep.mutate({ capabilityId, activityId, dependencyId: dep.id })}
                    className="text-gray-400 hover:text-red-500 flex-shrink-0"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex items-center gap-2">
          <select
            value={predecessorId}
            onChange={(e) => setPredecessorId(e.target.value)}
            className="input text-xs flex-1"
          >
            <option value="">Add predecessor...</option>
            {siblingActivities
              .filter(a => !deps.some(d => d.predecessorId === a.id))
              .map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
          </select>
          <select value={depType} onChange={(e) => setDepType(e.target.value as 'FS' | 'SS')} className="input text-xs w-16">
            <option value="FS">FS</option>
            <option value="SS">SS</option>
          </select>
          <input
            type="number"
            value={lagDays}
            onChange={(e) => setLagDays(e.target.value)}
            className="input text-xs w-16 text-center"
            placeholder="Lag"
            min="0"
          />
          <button
            onClick={() => {
              if (!predecessorId) return;
              addDep.mutate({ capabilityId, activityId, predecessorId, dependencyType: depType, lagDays: parseInt(lagDays) || 0 });
              setPredecessorId('');
              setLagDays('0');
            }}
            disabled={!predecessorId || addDep.isPending}
            className="btn-primary btn-sm text-xs"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
